import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { usePrivy } from '@privy-io/react-auth';
import WidthContainer from '../../components/WidthContainer';
import LoadingSpinner from '../../components/LoadingSpinner';
import GiftCard from '../../components/GiftCard';
import TextInput from '../../components/Inputs/Text';
import { getCheckoutTokenAddress } from '../../common/checkoutTokens';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
} from '../../redux/slices/tokenSlice';
import { useLazySearchTokensQuery } from '../../redux/api/public/tokens';
import { compressBase64Image } from '../../common/images';
import { openModal, closeModal } from '../../redux/slices/modals';
import {
  sendEvent,
  EVENT_STRIPE_CHECKOUT_VIEWED,
  EVENT_STRIPE_CHECKOUT_AMOUNT_CHANGED,
  EVENT_STRIPE_CHECKOUT_SENDER_NAME_ADDED,
  EVENT_STRIPE_CHECKOUT_MESSAGE_ADDED,
} from '../../common/analytics';
import { useCreateCustomGiftCardDesignMutation } from '../../redux/api/authorized/customGiftCardDesigns';
import { PHONE_MEDIA_QUERY } from '../../common/responsive';

const CHECKOUT_NETWORK = 'solana';

const AMOUNTS = [5, 25, 50, 100];

const CHECKOUT_IMAGE_STORAGE_PREFIX = 'deks-stripe-checkout-image-';

function getStoredImageKey(imageId) {
  return `${CHECKOUT_IMAGE_STORAGE_PREFIX}${imageId}`;
}

const Content = styled.div`
  padding-bottom: 120px;
  margin-left: auto;
  margin-right: auto;
  padding-top: 20px;
`;

const TokenLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.text.title};
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.text.secondary};
  text-align: center;
  margin-bottom: 12px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const Section = styled.section`
  width: 100%;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text.secondary};
  margin-bottom: 8px;
`;

const InputWrap = styled.div`
  margin-bottom: 16px;
`;

const InfoBlock = styled.div`
  padding: 16px 0px;
  border-radius: 12px;
`;

const InfoTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  margin-bottom: 8px;
`;

const InfoText = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.text.secondary};
  line-height: 1.5;
  margin: 0;
`;

const AmountButton = styled.button`
  flex: 1;
  border-radius: 16px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? `${props.theme.primary.primary}`
        : `${props.theme.border.primary}`};
  padding: 12px 12px;
  background-color: ${(props) =>
    props.$active
      ? `${props.theme.background.primary}`
      : `${props.theme.background.light}`};
  color: ${({ theme }) => theme.text.title};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$active
          ? `${props.theme.primary.primary}`
          : `${props.theme.text.disabled}`};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AmountButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const PaySection = styled.div`
  padding: 50px 0px 20px 0px;
  position: relative;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  background: linear-gradient(
    to top,
    ${({ theme }) => theme.background.primary} 0%,
    ${({ theme }) => theme.background.primary} 60%,
    transparent 100%
  );
  @media ${PHONE_MEDIA_QUERY} {
    position: fixed;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 600px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

function StripeCheckout() {
  const { t } = useTranslation();
  const { tokenName } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const tokenAddress = getCheckoutTokenAddress(tokenName);
  const token = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, CHECKOUT_NETWORK, tokenAddress ?? ''),
  );
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, CHECKOUT_NETWORK, 'USDC'),
  );
  const [searchTokens] = useLazySearchTokensQuery();

  const { authenticated } = usePrivy();

  const hasSentNameAddedRef = useRef(false);
  const hasSentMessageAddedRef = useRef(false);

  const amountFromQueryOnMount = searchParams.get('amount');

  const initialAmount =
    amountFromQueryOnMount && AMOUNTS.includes(Number(amountFromQueryOnMount))
      ? Number(amountFromQueryOnMount)
      : 50;

  const initialFrom = searchParams.get('from');
  const initialMessage = searchParams.get('message');
  const initialImageId = searchParams.get('imageId');
  const initialImageUrl = searchParams.get('image');

  const [selectedAmount, setSelectedAmount] = useState(initialAmount);
  const [cardImage, setCardImage] = useState(() => {
    try {
      return initialImageUrl ? decodeURIComponent(initialImageUrl) : null;
    } catch {
      return null;
    }
  });
  const [senderName, setSenderName] = useState(() => {
    try {
      return initialFrom ? decodeURIComponent(initialFrom) : '';
    } catch {
      return '';
    }
  });
  const [personalMessage, setPersonalMessage] = useState(() => {
    try {
      return initialMessage ? decodeURIComponent(initialMessage) : '';
    } catch {
      return '';
    }
  });
  const [initialImageLoaded, setInitialImageLoaded] = useState(!initialImageId);
  const amountFromQuery = searchParams.get('amount');
  const resolvedAmount =
    amountFromQuery && AMOUNTS.includes(Number(amountFromQuery))
      ? Number(amountFromQuery)
      : selectedAmount;

  useEffect(() => {
    sendEvent(EVENT_STRIPE_CHECKOUT_VIEWED, {
      token_name: tokenName ?? undefined,
    });
  }, [tokenName]);

  useEffect(() => {
    if (!tokenAddress) {
      navigate('/', { replace: true });
      return;
    }
    searchTokens({ addresses: [tokenAddress] });
  }, [tokenAddress, searchTokens, navigate]);

  // Load card image from localStorage when imageId is in URL
  useEffect(() => {
    if (!initialImageId || initialImageLoaded) return;
    try {
      const stored = localStorage.getItem(getStoredImageKey(initialImageId));
      if (stored) setCardImage(stored);
    } catch (e) {
      console.warn('Failed to load stripe checkout image from storage', e);
    }
    setInitialImageLoaded(true);
  }, [initialImageId, initialImageLoaded]);

  // Persist sender name and message to URL
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (senderName) next.set('from', encodeURIComponent(senderName));
        else next.delete('from');
        if (personalMessage)
          next.set('message', encodeURIComponent(personalMessage));
        else next.delete('message');
        return next;
      },
      { replace: true },
    );
  }, [senderName, personalMessage, setSearchParams]);

  const handleCardImageChange = useCallback(
    (newImage) => {
      const prevImageId = searchParams.get('imageId');
      if (newImage && newImage.startsWith('data:')) {
        const uuid = crypto.randomUUID();
        try {
          localStorage.setItem(getStoredImageKey(uuid), newImage);
          if (prevImageId) {
            try {
              localStorage.removeItem(getStoredImageKey(prevImageId));
            } catch (_) {}
          }
        } catch (e) {
          console.warn('Failed to store stripe checkout image', e);
        }
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('imageId', uuid);
            next.delete('image');
            return next;
          },
          { replace: true },
        );
      } else {
        if (prevImageId) {
          try {
            localStorage.removeItem(getStoredImageKey(prevImageId));
          } catch (_) {}
        }
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('imageId');
            if (newImage) {
              next.set('image', encodeURIComponent(newImage));
            } else {
              next.delete('image');
            }
            return next;
          },
          { replace: true },
        );
      }
      setCardImage(newImage);
    },
    [searchParams, setSearchParams],
  );

  const handleImageComplete = useCallback(
    (croppedDataUrl) => {
      handleCardImageChange(croppedDataUrl);
      dispatch(closeModal());
    },
    [handleCardImageChange, dispatch],
  );

  const handleImageCancel = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const handleCreateCustomImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageData = reader.result;
        if (!imageData) return;
        dispatch(
          openModal({
            name: 'cardImageCrop',
            props: {
              image: imageData,
              isPhysical: false,
              onComplete: handleImageComplete,
              onClose: handleImageCancel,
            },
          }),
        );
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, [dispatch, handleImageComplete, handleImageCancel]);

  const [createCustomGiftCardDesign] = useCreateCustomGiftCardDesignMutation();

  // Save local data URL image to backend when user is logged in; then switch to API URL
  useEffect(() => {
    const saveLocalImageIfNeeded = async () => {
      if (!cardImage || !cardImage.startsWith('data:')) return;
      if (!authenticated) return;

      try {
        const compressedImage = await compressBase64Image(cardImage);
        const response = await createCustomGiftCardDesign({
          image: compressedImage,
          type: 'digital',
        });
        if (response.data?.imageUrl) {
          const imageUrl = response.data.imageUrl;
          setCardImage(imageUrl);
          const imageId = searchParams.get('imageId');
          if (imageId) {
            try {
              localStorage.removeItem(getStoredImageKey(imageId));
            } catch (_) {}
          }
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('imageId');
              next.set('image', encodeURIComponent(imageUrl));
              return next;
            },
            { replace: true },
          );
        }
      } catch (error) {
        console.error('Failed to save local image:', error);
      }
    };
    saveLocalImageIfNeeded();
  }, [
    cardImage,
    authenticated,
    createCustomGiftCardDesign,
    searchParams,
    setSearchParams,
  ]);

  if (!tokenAddress) {
    return null;
  }

  if (!token) {
    return (
      <Content>
        <TokenLoadingContainer>
          <LoadingSpinner />
          <Subtitle as='p'>{t('common.loading')}</Subtitle>
        </TokenLoadingContainer>
      </Content>
    );
  }

  const tokenLabel =
    tokenName === 'yield'
      ? 'High interest'
      : token.name || token.symbol || tokenName;

  const giftCardToken = {
    address: token.address,
    symbol: token.symbol,
    logoURI: token.logoURI,
    giftCardImages: token.giftCardImages ?? [],
  };

  const OPEN_AI_DESIGN_NAMES = [
    'OPEN_AI.webp',
    'OPEN_AI_THANK_YOU.webp',
    'OPEN_AI_HAPPY_ANNIVERSARY.webp',
    'OPEN_AI_HAPPY_BIRTHDAY.webp',
  ];

  const BTC_DESIGN_NAMES = [
    'BTC.webp',
    'BTC_THANK_YOU.webp',
    'BTC_HAPPY_ANNIVERSARY.webp',
    'BTC_HAPPY_BIRTHDAY.webp',
    'BTC_JUST_BECAUSE.webp',
  ];

  const YIELD_DESIGN_NAMES = [
    'YIELD.webp',
    'YIELD_THANK_YOU.webp',
    'YIELD_HAPPY_ANNIVERSARY.webp',
    'YIELD_HAPPY_BIRTHDAY.webp',
    'YIELD_JUST_BECAUSE.webp',
  ];

  const GOLD_DESIGN_NAMES = [
    'GOLD.webp',
    'GOLD_THANK_YOU.webp',
    'GOLD_HAPPY_ANNIVERSARY.webp',
    'GOLD_HAPPY_BIRTHDAY.webp',
    'GOLD_JUST_BECAUSE.webp',
  ];

  const defaultDesignsBaseUrl =
    typeof import.meta.env.VITE_FE_HOST === 'string'
      ? `${import.meta.env.VITE_FE_HOST}/default-designs`
      : '/default-designs';

  const tokenNameLower = tokenName?.toLowerCase();

  const defaultImages = (() => {
    let designNames = null;

    if (tokenNameLower === 'openai') {
      designNames = OPEN_AI_DESIGN_NAMES;
    } else if (tokenNameLower === 'btc') {
      designNames = BTC_DESIGN_NAMES;
    } else if (tokenNameLower === 'yield') {
      designNames = YIELD_DESIGN_NAMES;
    } else if (tokenNameLower === 'gold') {
      designNames = GOLD_DESIGN_NAMES;
    }

    if (!designNames) return null;

    return designNames.map((name) => ({
      imageUrl: `${defaultDesignsBaseUrl}/${name}`,
    }));
  })();

  return (
    <Content>
      <ContentContainer>
        <div>
          <Title>
            {t('checkoutPage.giftCardTitle', {
              tokenLabel,
            })}
          </Title>
          <Subtitle>
            {t('checkoutPage.subtitle', {
              tokenLabel,
            })}
          </Subtitle>
        </div>

        <Section>
          <GiftCard
            token={giftCardToken}
            cardImage={cardImage}
            onCardImageChange={handleCardImageChange}
            defaultImages={defaultImages}
            disableEphemeralCustomImage={authenticated}
            onCreateCustomImage={handleCreateCustomImage}
            usdAmount={resolvedAmount}
            editable
            displayImageSelector
            hideValue={true}
          />
        </Section>

        <Section style={{ marginTop: '12px' }}>
          <AmountButtonContainer>
            {AMOUNTS.map((value) => (
              <AmountButton
                key={value}
                $active={resolvedAmount === value}
                onClick={() => {
                  setSelectedAmount(value);
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('amount', String(value));
                      return next;
                    },
                    { replace: true },
                  );
                  sendEvent(EVENT_STRIPE_CHECKOUT_AMOUNT_CHANGED, {
                    amount: value,
                    token_name: tokenName ?? undefined,
                  });
                }}
              >
                ${value}
              </AmountButton>
            ))}
          </AmountButtonContainer>
        </Section>

        <Section style={{ marginTop: '12px' }}>
          <InputWrap>
            <FieldLabel>{t('giftFlow.customize.fromOptional')}</FieldLabel>
            <TextInput
              placeholder={t('giftFlow.customize.yourNamePlaceholder')}
              value={senderName}
              onChange={(e) => {
                const value = e.target.value;
                setSenderName(value);
                if (value.trim() && !hasSentNameAddedRef.current) {
                  hasSentNameAddedRef.current = true;
                  sendEvent(EVENT_STRIPE_CHECKOUT_SENDER_NAME_ADDED, {
                    token_name: tokenName ?? undefined,
                  });
                }
              }}
            />
          </InputWrap>
          <InputWrap>
            <FieldLabel>
              {t('giftFlow.customize.personalMessageOptional')}
            </FieldLabel>
            <TextInput
              placeholder={t(
                'giftFlow.customize.writePersonalMessagePlaceholder',
              )}
              value={personalMessage}
              onChange={(e) => {
                const value = e.target.value;
                setPersonalMessage(value);
                if (value.trim() && !hasSentMessageAddedRef.current) {
                  hasSentMessageAddedRef.current = true;
                  sendEvent(EVENT_STRIPE_CHECKOUT_MESSAGE_ADDED, {
                    token_name: tokenName ?? undefined,
                  });
                }
              }}
            />
          </InputWrap>
        </Section>

        <InfoBlock>
          <InfoTitle>{t('checkoutPage.howGiftDelivered')}</InfoTitle>
          <InfoText>{t('checkoutPage.giftDeliveredDescription')}</InfoText>
        </InfoBlock>
        <InfoBlock>
          <InfoTitle>{t('checkoutPage.howItWorks')}</InfoTitle>
          <InfoText>{t('checkoutPage.howItWorksDescription')}</InfoText>
        </InfoBlock>

        <PaySection>
          <WidthContainer>
            <div
              style={
                resolvedAmount === 5
                  ? {
                      opacity: 1,
                      justifyContent: 'center',
                      display: 'flex',
                      position: 'relative',
                    }
                  : { opacity: 0, position: 'absolute', zIndex: -1 }
              }
            >
              <stripe-buy-button
                buy-button-id='buy_btn_1T27EQGWZyVifLGlvJlGGGgx'
                publishable-key='pk_live_51Ra0jOGWZyVifLGlRD3rGxjKHXJZR0KhD1EGr07vO4983PnfqC6DejTwHmSHJuMB6if5qH9Ndr5jOT2LC2fCZe5x00EO88QvFP'
              ></stripe-buy-button>
            </div>
            <div
              style={
                resolvedAmount === 25
                  ? { opacity: 1, justifyContent: 'center', display: 'flex' }
                  : { opacity: 0, position: 'absolute', zIndex: -1 }
              }
            >
              <stripe-buy-button
                buy-button-id='buy_btn_1T2AhkGWZyVifLGlr6UCIaHk'
                publishable-key='pk_live_51Ra0jOGWZyVifLGlRD3rGxjKHXJZR0KhD1EGr07vO4983PnfqC6DejTwHmSHJuMB6if5qH9Ndr5jOT2LC2fCZe5x00EO88QvFP'
              ></stripe-buy-button>
            </div>
            <div
              style={
                resolvedAmount === 50
                  ? { opacity: 1, justifyContent: 'center', display: 'flex' }
                  : { opacity: 0, position: 'absolute', zIndex: -1 }
              }
            >
              <stripe-buy-button
                buy-button-id='buy_btn_1T2AjRGWZyVifLGlm0xu6bzL'
                publishable-key='pk_live_51Ra0jOGWZyVifLGlRD3rGxjKHXJZR0KhD1EGr07vO4983PnfqC6DejTwHmSHJuMB6if5qH9Ndr5jOT2LC2fCZe5x00EO88QvFP'
              ></stripe-buy-button>
            </div>
            <div
              style={
                resolvedAmount === 100
                  ? { opacity: 1, justifyContent: 'center', display: 'flex' }
                  : { opacity: 0, position: 'absolute', zIndex: -1 }
              }
            >
              <stripe-buy-button
                buy-button-id='buy_btn_1T2AkSGWZyVifLGlReD0k5OF'
                publishable-key='pk_live_51Ra0jOGWZyVifLGlRD3rGxjKHXJZR0KhD1EGr07vO4983PnfqC6DejTwHmSHJuMB6if5qH9Ndr5jOT2LC2fCZe5x00EO88QvFP'
              ></stripe-buy-button>
            </div>
          </WidthContainer>
        </PaySection>
      </ContentContainer>
    </Content>
  );
}

export default StripeCheckout;
